import { memo, useCallback, useEffect } from "react";
import { PaymentType } from "shared/utils/payment";
import Radio, { RadioVariant } from "shared/ui/radio";
import Heading from "shared/ui/typography/Heading";
import Text from "shared/ui/typography/Text";
import { PaymentActions } from "shared/utils/payment-methods";
import PaymentMethodList from "features/shared/payment-method-list";
import { concatClassnames } from "shared/utils/concatClassnames";
import { OrderType } from "../../../pages/payment/providers/PaymentProvider";
import { useOrderContext } from "app/providers/OrderProvider";
import { useCartContext } from "app/providers/CartProvider";
import { clearVoucher, voucherState } from "shared/store/voucher/voucherStore";
import { useSnapshot } from "valtio";

export enum PaymentMethod {
  DEFERRED_PAYMENT,
  PARTIAL_PAYMENT,
  CUSTOM_PAYMENT,
  WARRANTY_OWN_RIST,
}

enum PaymentMethodTypeKey {
  PAY_NOW = "pay_now",
  PAY_FULL = "pay_full",
  PAY_CUSTOM = "pay_custom",
  PAY_LATER_TERMS = "pay_later_terms",
  PAY_LATER_MONTHS = "pay_later_months",
  BANK_TRANSFER = "bank_transfer",
}

const DOOR_PAYMENT_METHOD_TEXT: Record<
  PaymentMethod,
  { [key: string]: { title: string; description: string } }
> = {
  [PaymentMethod.DEFERRED_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_NOW]: {
      title: "Betaal nu 10% en de rest pas na montage",
      description: `De overige 90% betaal je na levering en montage. 
      Je beoordeelt samen met de monteur het resultaat en als je
      helemaal tevreden bent betaal je het restant.`,
    },
    [PaymentMethodTypeKey.PAY_FULL]: {
      title:
        "Betaal nu alles in 1 keer & ontvang gratis GewoonZeker voor 1 jaar",
      description:
        "Wil je je bestelling in één keer betalen? Dan ontvang je 1 jaar het GewoonZeker zekerheidspakket (t.w.v. €39,-) cadeau.",
    },
    [PaymentMethodTypeKey.PAY_LATER_TERMS]: {
      title: "Betaal het totaalbedrag met Klarna",
      description: `Wil je graag achteraf of gespreid betalen? 
        Klarna biedt de mogelijkheid om je gehele bestelling binnen 30 dagen of in 3 automatische termijnen te betalen. 
        Bij betalen in 3 delen betaal je het eerste termijn na levering en montage.`,
    },
    [PaymentMethodTypeKey.PAY_LATER_MONTHS]: {
      title:
        "Betaal het totaalbedrag gespreid in 12 tot 60 maanden met SprayPay",
      description: `Wil je graag gespreid betalen? SprayPay biedt de mogelijkheid om je 
      gehele bestelling in 12, 18, 24, 36 of 60 maanden te betalen. 
      Het eerste termijn wordt afgeschreven 30 dagen na goedkeuring van SprayPay.`,
    },
    [PaymentMethodTypeKey.BANK_TRANSFER]: {
      title: "Betaling op factuur",
      description: `Ontvang eerst je bestelling en betaal daarna binnen 14 dagen.`,
    },
  },
  [PaymentMethod.PARTIAL_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_FULL]: {
      title: "Betaal direct het openstaande bedrag",
      description: "",
    },
  },
  [PaymentMethod.CUSTOM_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_CUSTOM]: {
      title: "Betaal direct het openstaande bedrag",
      description: "",
    },
  },
  [PaymentMethod.WARRANTY_OWN_RIST]: {
    [PaymentMethodTypeKey.PAY_CUSTOM]: {
      title: "Betaal het eigen risico van € 149,-",
      description: "",
    },
  },
};

const ZakelijPaymentText = {
  title: "Betaal direct via iDeal",
  description: "Dat is wel zo gemakkelijk en ben je meteen van alles af.",
};

const SELF_ASSEMBLY_DOOR_PAYMENT_METHOD_TEXT: Record<
  PaymentMethod,
  { [key: string]: { title: string; description: string } }
> = {
  [PaymentMethod.DEFERRED_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_NOW]: {
      title: "Betaal nu 10% en de rest pas na montage",
      description: `De overige 90% betaal je na levering en montage. 
      Je beoordeelt samen met de monteur het resultaat en als je
      helemaal tevreden bent betaal je het restant.`,
    },
    [PaymentMethodTypeKey.PAY_FULL]: {
      title: "Bestelling afrekenen",
      description:
        "Na betaling wordt je bestelling in order gemaakt en bezorgd op de door jou gekozen datum",
    },
    [PaymentMethodTypeKey.PAY_LATER_MONTHS]: {
      title: "Betaal gespreid in 12 tot 60 maanden met SprayPay",
      description: `Wil je graag gespreid betalen? SprayPay biedt de mogelijkheid om je gehele bestelling in 12, 18, 24, 36 of 60 maanden te betalen. De eerste termijn wordt afgeschreven 30 dagen na goedkeuring van SprayPay.`,
    },
  },
  [PaymentMethod.PARTIAL_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_FULL]: {
      title: "Betaal direct het openstaande bedrag",
      description: "",
    },
  },
  [PaymentMethod.CUSTOM_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_CUSTOM]: {
      title: "TODOTEXT: title of custom payment",
      description: `TODOTEXT: body of custom payment`,
    },
  },
  [PaymentMethod.WARRANTY_OWN_RIST]: {
    [PaymentMethodTypeKey.PAY_CUSTOM]: {
      title: "Betaal het eigen risico van € 149,-",
      description: "",
    },
  },
};

const SAMPLE_SALE_DOOR_PAYMENT_METHOD_TEXT: Record<
  PaymentMethod.DEFERRED_PAYMENT,
  { [key: string]: { title: string; description: string } }
> = {
  [PaymentMethod.DEFERRED_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_FULL]: {
      title: "Betaal nu voor je sample sale deur using",
      description: "",
    },
    [PaymentMethodTypeKey.PAY_LATER_MONTHS]: {
      title:
        "Betaal het totaalbedrag gespreid in 12 tot 60 maanden met SprayPay",
      description: "",
    },
  },
};

const STANDARD_PRODUCT_PAYMENT_METHOD_TEXT: Record<
  PaymentMethod,
  { [key: string]: { title: string; description: string } }
> = {
  [PaymentMethod.DEFERRED_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_NOW]: {
      title: "Betaal nu 10% en de rest pas na montage",
      description: `De overige 90% betaal je na levering en montage. 
      Je beoordeelt samen met de monteur het resultaat en als je
      helemaal tevreden bent betaal je het restant.`,
    },
    [PaymentMethodTypeKey.PAY_FULL]: {
      title: "Bestelling afrekenen ",
      description:
        "Na betaling wordt je bestelling direct in orde gemaakt en verzonden.",
    },
    [PaymentMethodTypeKey.PAY_LATER_TERMS]: {
      title: "Betaal het totaalbedrag met Klarna",
      description: `Wil je graag achteraf of gespreid betalen? 
        Klarna biedt de mogelijkheid om je gehele bestelling binnen 30 dagen of in 3 automatische termijnen te betalen. 
        Bij betalen in 3 delen betaal je het eerste termijn na levering en montage.`,
    },
    [PaymentMethodTypeKey.PAY_LATER_MONTHS]: {
      title:
        "Betaal het totaalbedrag gespreid in 12 tot 60 maanden met SprayPay",
      description: `Wil je graag gespreid betalen? SprayPay biedt de mogelijkheid om je 
      gehele bestelling in 12, 18, 24, 36 of 60 maanden te betalen. 
      Het eerste termijn wordt afgeschreven 30 dagen na goedkeuring van SprayPay.`,
    },
  },
  [PaymentMethod.PARTIAL_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_FULL]: {
      title: "Betaal direct het openstaande bedrag",
      description: "",
    },
  },
  [PaymentMethod.CUSTOM_PAYMENT]: {
    [PaymentMethodTypeKey.PAY_CUSTOM]: {
      title: "Betaal direct het openstaande bedrag",
      description: "",
    },
  },
  [PaymentMethod.WARRANTY_OWN_RIST]: {
    [PaymentMethodTypeKey.PAY_CUSTOM]: {
      title: "Betaal het eigen risico van € 149,-",
      description: "",
    },
  },
};

interface PaymentMethodsProps {
  types: PaymentType[];
  method: PaymentMethod;
  paymentAction: PaymentActions | null;
  setPaymentAction: React.Dispatch<React.SetStateAction<PaymentActions | null>>;

  orderType?: OrderType;
  className?: string;
  isSampleSale?: boolean;
}

const PaymentMethods = ({
  types,
  method,
  paymentAction,
  setPaymentAction,

  orderType = OrderType.DOOR,
  className = "",
  isSampleSale = false,
}: PaymentMethodsProps) => {
  const { isOrderAssembly, order } = useOrderContext();
  const { containsZakelijkProducts } = useCartContext();
  const setDefaultPaymentAction = useCallback(() => {
    if (paymentAction) return;

    if (types && types[0]) {
      setPaymentAction(types[0].action);
    }
  }, [paymentAction, setPaymentAction, types]);

  useEffect(() => {
    setDefaultPaymentAction();
  }, [setDefaultPaymentAction]);

  const { voucher } = useSnapshot(voucherState);
  const hasWarrantyVoucher =
    voucher?.warrantyPackages && voucher.warrantyPackages.length > 0;

  //Temporary fix for zakelijk products
  const radioName = !containsZakelijkProducts ? "payment_type" : undefined;

  const onSelect = useCallback(
    (action: PaymentActions) => {
      setPaymentAction(action);
      if (hasWarrantyVoucher) clearVoucher();
    },
    [setPaymentAction, hasWarrantyVoucher],
  );

  return (
    <div className={concatClassnames(className)}>
      <Heading headingLevel={"h3"} className="gg-mb-6">
        Kies een betaalmethode
      </Heading>

      <div className="gg-grid gg-gap-y-5">
        {types.map((type) => {
          const methodName = type.action.toString().toLowerCase();
          let textMapping =
            orderType === OrderType.DOOR
              ? isSampleSale
                ? SAMPLE_SALE_DOOR_PAYMENT_METHOD_TEXT[
                    PaymentMethod.DEFERRED_PAYMENT
                  ][methodName as PaymentMethodTypeKey]
                : isOrderAssembly
                  ? SELF_ASSEMBLY_DOOR_PAYMENT_METHOD_TEXT[method][
                      methodName as PaymentMethodTypeKey
                    ]
                  : DOOR_PAYMENT_METHOD_TEXT[method][
                      methodName as PaymentMethodTypeKey
                    ]
              : STANDARD_PRODUCT_PAYMENT_METHOD_TEXT[method][
                  methodName as PaymentMethodTypeKey
                ];

          if (
            order?.isZakelijkOrder &&
            methodName === PaymentMethodTypeKey.PAY_FULL
          ) {
            textMapping = ZakelijPaymentText;
          }

          return (
            <div key={`payment-method-${methodName}`}>
              <div className="gg-flex gg-align-middle">
                <Radio
                  variant={RadioVariant.CIRCLE}
                  name={radioName}
                  id={methodName}
                  onChange={() => onSelect(type.action)}
                  checked={type.action === paymentAction}
                />
                <label
                  htmlFor={methodName}
                  className="gg-text-md gg-align-middle"
                >
                  <Heading headingLevel="h5" className="gg-font-bold">
                    {textMapping.title}
                  </Heading>

                  <Text className="gg-text-sm">{textMapping.description}</Text>
                </label>
              </div>

              {type.methods.length > 0 && paymentAction === type.action && (
                <PaymentMethodList
                  className="gg-mt-5 gg-pl-6"
                  paymentType={type}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default PaymentMethods;
